// Executive Challenge backend — Phase 2 SUPABASE implementation.
//
// Talks to Supabase over plain fetch (Auth REST + PostgREST) instead of
// @supabase/supabase-js: the client library is ~30KB of gzip for the four
// requests we actually make, and the worker bundle is size-sensitive. The
// schema + RLS this code assumes live in supabase/migrations/0001_challenges.sql.
//
// Identity: Supabase Auth phone OTP. We never see or store the raw phone
// number — it lives in Supabase's managed auth.users; everything here keys on
// the session's auth uid. The 4-char player ID rides on the auth user as
// user_metadata.player_id so it follows the person across challenges.
//
// Session persistence: the session JSON is stored under a dedicated key in
// idb.meta's existing key-value "attributes" store (same pattern as the
// credits balance). Adding a key to that store is type-only — no meta DB
// version bump or migration — which is why this beats a new object store.
// Sessions are read through on every call rather than cached in module state:
// these are rare user actions (create/join/submit), and reading through keeps
// worker restarts and multiple tabs trivially consistent.

import { idb } from "../../db/index.ts";
import {
	CHALLENGE_CODE_LENGTH,
	isValidChallengeCode,
	seedForCode,
} from "../../../common/executiveChallenge.ts";
import {
	SUPABASE_ANON_KEY,
	SUPABASE_AUTH_MODE,
	SUPABASE_URL,
} from "./config.ts";
import { ChallengeCodeError } from "./types.ts";
import type { ChallengeEntry, ChallengeRow } from "./types.ts";

const SESSION_KEY = "challengeSession";

// Refresh this long before the JWT actually lapses, so a request started with
// a "fresh" token can't expire mid-flight.
const EXPIRY_MARGIN_MS = 30 * 1000;

const CREATE_CODE_ATTEMPTS = 5;

type StoredSession = {
	accessToken: string;
	refreshToken: string;
	// ms epoch when accessToken expires
	expiresAt: number;
	uid: string;
	playerId?: string;
};

// What Supabase Auth returns from /verify and /token.
type AuthTokenResponse = {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	user: {
		id: string;
		user_metadata?: {
			player_id?: string;
		};
	};
};

const loadSession = async (): Promise<StoredSession | undefined> => {
	const raw = await idb.meta.get("attributes", SESSION_KEY);
	if (typeof raw !== "string") {
		return;
	}
	try {
		return JSON.parse(raw) as StoredSession;
	} catch {
		// Corrupt blob — treat as signed out rather than wedging every call.
		return;
	}
};

const saveSession = async (session: StoredSession): Promise<void> => {
	await idb.meta.put("attributes", JSON.stringify(session), SESSION_KEY);
};

const clearSession = async (): Promise<void> => {
	await idb.meta.delete("attributes", SESSION_KEY);
};

const toStoredSession = (data: AuthTokenResponse): StoredSession => ({
	accessToken: data.access_token,
	refreshToken: data.refresh_token,
	expiresAt: Date.now() + data.expires_in * 1000,
	uid: data.user.id,
	playerId: data.user.user_metadata?.player_id,
});

const authHeaders = (accessToken?: string): Record<string, string> => ({
	apikey: SUPABASE_ANON_KEY,
	// PostgREST requires a bearer even for anonymous reads; the anon key is
	// itself a valid (unprivileged) JWT for that.
	Authorization: `Bearer ${accessToken ?? SUPABASE_ANON_KEY}`,
	"Content-Type": "application/json",
});

// Surface the server's error message when there is one; HTTP status otherwise.
const errorFromResponse = async (
	res: Response,
	fallback: string,
): Promise<Error> => {
	let detail = "";
	try {
		const body = await res.json();
		detail = body.msg ?? body.message ?? body.error_description ?? "";
	} catch {
		// Non-JSON error body — the status alone will have to do.
	}
	return new Error(`${fallback} (${res.status}${detail ? `: ${detail}` : ""})`);
};

// The session with a live access token, refreshing through Supabase Auth when
// the stored one has (nearly) expired. Undefined means signed out — either no
// session or a refresh the server rejected (revoked/expired refresh token).
const freshSession = async (): Promise<StoredSession | undefined> => {
	const session = await loadSession();
	if (!session) {
		return;
	}
	if (Date.now() < session.expiresAt - EXPIRY_MARGIN_MS) {
		return session;
	}

	const res = await fetch(
		`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,
		{
			method: "POST",
			headers: authHeaders(),
			body: JSON.stringify({ refresh_token: session.refreshToken }),
		},
	);
	if (!res.ok) {
		// The server rejected the refresh token — that session is dead for good,
		// so drop it and make the user re-verify. (A network failure would have
		// thrown out of fetch instead, keeping the stored session for retry.)
		await clearSession();
		return;
	}
	const refreshed = toStoredSession((await res.json()) as AuthTokenResponse);
	// Refresh responses can omit user_metadata; don't lose the player ID.
	refreshed.playerId = refreshed.playerId ?? session.playerId;
	await saveSession(refreshed);
	return refreshed;
};

// The session every authenticated call runs on. In "anonymous" auth mode a
// missing session is minted on demand — the device's first challenge action
// signs it in, no SMS anywhere. In "phone" mode a missing session stays
// missing until the verify sheet runs the OTP. Anonymous users keep their uid
// if a phone is ever linked later, so entries survive the upgrade to real
// verification.
const ensureSession = async (): Promise<StoredSession | undefined> => {
	const existing = await freshSession();
	if (existing || SUPABASE_AUTH_MODE !== "anonymous") {
		return existing;
	}

	const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
		method: "POST",
		headers: authHeaders(),
		// No email/phone = an anonymous sign-in (requires the project's "Allow
		// anonymous sign-ins" toggle).
		body: JSON.stringify({ data: {} }),
	});
	if (!res.ok) {
		throw await errorFromResponse(res, "Could not create a player identity");
	}
	const session = toStoredSession((await res.json()) as AuthTokenResponse);
	await saveSession(session);
	return session;
};

export const getVerifiedSession = async (): Promise<{
	uid: string;
	playerId?: string;
} | null> => {
	try {
		const session = await ensureSession();
		return session ? { uid: session.uid, playerId: session.playerId } : null;
	} catch {
		// Offline: can't prove (or mint) a session, so report unverified and let
		// the UI try again when connectivity returns.
		return null;
	}
};

// Ask Supabase Auth to text a one-time code. Rate limiting, code generation,
// and expiry are all the provider's problem — that's why we don't roll our own.
export const requestOtp = async (phone: string): Promise<void> => {
	const res = await fetch(`${SUPABASE_URL}/auth/v1/otp`, {
		method: "POST",
		headers: authHeaders(),
		body: JSON.stringify({ phone }),
	});
	if (!res.ok) {
		throw await errorFromResponse(res, "Could not send the verification code");
	}
};

export const verifyOtp = async ({
	phone,
	token,
	playerId,
}: {
	phone: string;
	token: string;
	playerId?: string;
}): Promise<{ uid: string }> => {
	const res = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
		method: "POST",
		headers: authHeaders(),
		body: JSON.stringify({ phone, token, type: "sms" }),
	});
	if (!res.ok) {
		throw await errorFromResponse(res, "That code didn't verify");
	}
	const session = toStoredSession((await res.json()) as AuthTokenResponse);

	// First verify (or an edit): pin the player ID to the auth user so it
	// follows the phone across devices and challenges.
	if (playerId && playerId !== session.playerId) {
		await putPlayerId(session, playerId);
	}

	await saveSession(session);
	return { uid: session.uid };
};

// PUT the handle onto the auth user's metadata. A failure is cosmetic (the
// entry row carries its own player_id), so callers treat this as best-effort.
const putPlayerId = async (
	session: StoredSession,
	playerId: string,
): Promise<void> => {
	const userRes = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
		method: "PUT",
		headers: authHeaders(session.accessToken),
		body: JSON.stringify({ data: { player_id: playerId } }),
	});
	if (userRes.ok) {
		session.playerId = playerId;
	}
};

// Pin (or change) the player ID after the fact — the verify sheet's flow is
// identity first, initials second, so the session exists (or, in anonymous
// mode, is minted here) before the ID is chosen.
export const setPlayerId = async (playerId: string): Promise<void> => {
	const session = await ensureSession();
	if (!session) {
		throw new Error("ChallengeNotVerified");
	}
	if (session.playerId === playerId) {
		return;
	}
	await putPlayerId(session, playerId);
	await saveSession(session);
};

// PostgREST reports unique violations as 409; everything keyed on uniqueness
// (code minting, one-entry-per-person) branches on this.
const isConflict = (res: Response) => res.status === 409;

// Mint a join code by insert-and-retry: generate a random 6-digit candidate,
// let the primary key arbitrate uniqueness, re-roll on conflict. The seed is
// derived (not random), so computing it client-side loses nothing.
export const createChallengeRow = async ({
	tid,
	scenarioId,
	hostPlayerId,
}: {
	tid: number;
	scenarioId: string;
	hostPlayerId: string;
}): Promise<ChallengeRow> => {
	const session = await ensureSession();
	if (!session) {
		throw new Error("ChallengeNotVerified");
	}

	for (let attempt = 0; attempt < CREATE_CODE_ATTEMPTS; attempt++) {
		// 6 digits, no leading zero: [100000, 999999].
		const code = String(100000 + Math.floor(Math.random() * 900000));
		const seed = seedForCode(code);

		const res = await fetch(`${SUPABASE_URL}/rest/v1/challenges`, {
			method: "POST",
			headers: {
				...authHeaders(session.accessToken),
				// Get the inserted row back in one round trip.
				Prefer: "return=representation",
			},
			body: JSON.stringify({
				code,
				scenario_id: scenarioId,
				seed,
				tid,
				host_player_id: hostPlayerId,
				// host_uid comes from auth.uid() server-side — never client-asserted.
			}),
		});

		if (isConflict(res)) {
			// Someone holds this code — roll a new one. ~900k codes, so back-to-
			// back conflicts mean something is wrong, hence the attempt cap.
			continue;
		}
		if (!res.ok) {
			throw await errorFromResponse(res, "Could not create the challenge");
		}
		const [row] = (await res.json()) as {
			code: string;
			seed: string;
			tid: number;
		}[];
		if (!row) {
			// return=representation always echoes the inserted row; an empty body
			// means the server did something we don't understand.
			throw new Error("Challenge insert returned no row");
		}
		return { code: row.code, seed: row.seed, tid: row.tid };
	}

	throw new Error(
		`Could not mint a unique challenge code in ${CREATE_CODE_ATTEMPTS} attempts`,
	);
};

// Resolve a join code to the world it names. Public read — a joiner is not
// signed in yet when the join sheet previews the team.
export const getChallengeRow = async (code: string): Promise<ChallengeRow> => {
	if (!isValidChallengeCode(code)) {
		throw new ChallengeCodeError(
			`Enter the ${CHALLENGE_CODE_LENGTH}-digit code from your host.`,
		);
	}

	const res = await fetch(
		`${SUPABASE_URL}/rest/v1/challenges?code=eq.${code}&select=code,seed,tid,status`,
		{ headers: authHeaders() },
	);
	if (!res.ok) {
		throw await errorFromResponse(res, "Could not look up that code");
	}
	const rows = (await res.json()) as {
		code: string;
		seed: string;
		tid: number;
		status: string;
	}[];
	if (rows.length === 0) {
		throw new ChallengeCodeError("No challenge with that code.");
	}
	const row = rows[0]!;
	if (row.status === "closed") {
		throw new ChallengeCodeError("This challenge is closed to new entries.");
	}
	return { code: row.code, seed: row.seed, tid: row.tid };
};

export const submitEntry = async (
	entry: ChallengeEntry,
): Promise<{ shareId: string; shareExpiresAt: number }> => {
	const session = await ensureSession();
	if (!session) {
		throw new Error("ChallengeNotVerified");
	}

	const res = await fetch(`${SUPABASE_URL}/rest/v1/challenge_entries`, {
		method: "POST",
		headers: {
			...authHeaders(session.accessToken),
			// id and expires_at are minted by the database; representation hands
			// them back without a second query.
			Prefer: "return=representation",
		},
		body: JSON.stringify({
			code: entry.code,
			player_id: entry.playerId,
			gm_score: entry.gmScore,
			grade: entry.grade,
			draft_pick: entry.draftPick,
			wins: entry.wins,
			losses: entry.losses,
			// auth_uid comes from auth.uid() server-side — never client-asserted.
		}),
	});

	if (isConflict(res)) {
		// unique(code, auth_uid): this phone already has an entry for this code.
		// That's the "cannot re-do" rule working, not an infrastructure failure.
		throw new Error("You already submitted an entry for this challenge.");
	}
	if (!res.ok) {
		throw await errorFromResponse(res, "Could not submit your entry");
	}

	const [row] = (await res.json()) as { id: string; expires_at: string }[];
	if (!row) {
		throw new Error("Entry insert returned no row");
	}
	return {
		// The entry id doubles as the share id; expires_at gates only the share
		// link — the leaderboard row itself persists.
		shareId: row.id,
		shareExpiresAt: Date.parse(row.expires_at),
	};
};

// Per-challenge leaderboard. Reads go through the challenge_entries_public
// view, which strips auth_uid — the base table has no public select policy.
export const getLeaderboard = async (
	code: string,
): Promise<ChallengeEntry[]> => {
	const res = await fetch(
		`${SUPABASE_URL}/rest/v1/challenge_entries_public?code=eq.${code}&select=code,player_id,gm_score,grade,draft_pick,wins,losses&order=gm_score.desc`,
		{ headers: authHeaders() },
	);
	if (!res.ok) {
		throw await errorFromResponse(res, "Could not load the leaderboard");
	}
	const rows = (await res.json()) as {
		code: string;
		player_id: string;
		gm_score: number | string;
		grade: string;
		draft_pick: number | null;
		wins: number;
		losses: number;
	}[];
	return rows.map((row) => ({
		code: row.code,
		playerId: row.player_id,
		// numeric columns arrive as strings from PostgREST unless the schema uses
		// float8; coerce so ranking math never string-compares.
		gmScore: Number(row.gm_score),
		grade: row.grade,
		draftPick: row.draft_pick,
		wins: row.wins,
		losses: row.losses,
	}));
};
